// Ré-exporte cn depuis les utilitaires partagés
export { cn } from "@repo/utils";

import type { DonneesEntStruct } from "./types";

/**
 * Calcule la progression de l'entretien structuré
 * @param questionsEtat - État des questions depuis evaluation.donnees.questionsEtat
 * @param questionCouranteId - ID de la question courante
 * @param totalQuestionsTemplate - Nombre total de questions dans le template (optionnel)
 * @returns Objet contenant completed, total et percentage
 *
 * Note: Le total est calculé à partir des questionId UNIQUES pour éviter
 * que les questions de relance (même questionId) ne faussent le compteur.
 * Si totalQuestionsTemplate est fourni, il est utilisé comme total (plus précis).
 */
export function calculerProgressionQuestions(
  questionsEtat: DonneesEntStruct["questionsEtat"],
  questionCouranteId?: string,
  totalQuestionsTemplate?: number,
): { completed: number; total: number; percentage: number } {
  // Si pas de questions, retourner 0
  if (!questionsEtat || questionsEtat.length === 0) {
    return { completed: 0, total: totalQuestionsTemplate ?? 0, percentage: 0 };
  }

  // Extraire les questionId uniques (élimine les doublons des questions de relance)
  const questionIdUniques = [
    ...new Set(questionsEtat.map((q) => q.questionId)),
  ];

  // Utiliser le total du template si fourni, sinon utiliser les questionId uniques
  const total = totalQuestionsTemplate ?? questionIdUniques.length;

  // Si pas de question courante:
  // - Si on a des questionsEtat, l'entretien est terminé → 100%
  // - Sinon, l'entretien n'a pas commencé → 0%
  if (!questionCouranteId) {
    if (questionsEtat.length > 0) {
      // L'entretien est terminé, afficher 100%
      return { completed: total, total, percentage: 100 };
    }
    return { completed: 0, total, percentage: 0 };
  }

  // Trouver l'index de la question courante dans les questionId uniques
  const currentIndex = questionIdUniques.indexOf(questionCouranteId);

  // L'index représente le nombre de questions déjà complétées
  const completed = currentIndex >= 0 ? currentIndex : 0;
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

  return { completed, total, percentage };
}
